import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar as CalendarIcon, MapPin, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';

import { BACKEND_URL } from '../lib/api';
import NotifyMeForm from '../components/NotifyMeForm';

// SAST (UTC+2, no DST) — used to disable past dates in the calendar.
const sastToday = () => {
  const d = new Date();
  const sast = new Date(d.getTime() + (d.getTimezoneOffset() + 120) * 60 * 1000);
  sast.setHours(0, 0, 0, 0);
  return sast;
};
const toISODate = (d) => {
  // Local Y-M-D without TZ drift (we already constructed `d` in SAST).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fromISODate = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const formatPretty = (s) => {
  const d = fromISODate(s);
  if (!d) return '';
  return d.toLocaleDateString('en-ZA', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
};

const EventsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || '');
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    fetchEvents();
    // Sync filter state → URL so search results are shareable.
    const next = new URLSearchParams();
    if (category && category !== 'all') next.set('category', category);
    if (location) next.set('location', location);
    if (searchQuery) next.set('q', searchQuery);
    if (selectedDate) next.set('date', selectedDate);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, location, searchQuery, selectedDate]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.append('category', category);
      if (location) params.append('location', location);
      if (searchQuery) params.append('q', searchQuery);
      if (selectedDate) params.append('date', selectedDate);

      const response = await axios.get(`${BACKEND_URL}/api/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearDate = () => setSelectedDate('');
  const clearAll = () => {
    setCategory('all');
    setLocation('');
    setSearchQuery('');
    setSelectedDate('');
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-bold text-[#112318] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="events-page-title"
          >
            All Events
          </h1>
          <p className="text-base leading-relaxed text-[#4A5D50]">
            Discover premium wine farm experiences across South Africa
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-testid="filters-section">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-[#4A5D50]" />
            <h3 className="font-semibold text-[#112318]">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#4A5D50] mb-2">Search</label>
              <Input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
                data-testid="search-filter"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#4A5D50] mb-2">Find experiences by date</label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={`w-full justify-start text-left font-normal ${selectedDate ? 'text-[#112318]' : 'text-[#9AA89F]'}`}
                    data-testid="date-filter-trigger"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2 text-[#9C7434]" />
                    {selectedDate ? formatPretty(selectedDate) : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="date-filter-popover">
                  <Calendar
                    mode="single"
                    selected={fromISODate(selectedDate)}
                    onSelect={(d) => {
                      if (d) {
                        setSelectedDate(toISODate(d));
                        setCalendarOpen(false);
                      } else {
                        setSelectedDate('');
                      }
                    }}
                    disabled={{ before: sastToday() }}
                    initialFocus
                  />
                  {selectedDate && (
                    <div className="border-t border-stone-200 p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedDate('');
                          setCalendarOpen(false);
                        }}
                        className="w-full text-xs text-[#4A5D50]"
                        data-testid="date-filter-clear-popover"
                      >
                        <X className="w-3 h-3 mr-1" /> Clear date
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#4A5D50] mb-2">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-full" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Wine Tastings">Wine Tastings</SelectItem>
                  <SelectItem value="Food & Pairings">Food & Pairings</SelectItem>
                  <SelectItem value="Live Music">Live Music</SelectItem>
                  <SelectItem value="Markets & Pop-Ups">Markets & Pop-Ups</SelectItem>
                  <SelectItem value="Lifestyle Events">Lifestyle Events</SelectItem>
                  <SelectItem value="Special Events">Special Events</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#4A5D50] mb-2">Location</label>
              <Input
                type="text"
                placeholder="Search by location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full"
                data-testid="location-filter"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={clearAll}
                variant="outline"
                className="w-full"
                data-testid="clear-filters-button"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Date filter status banner */}
        {selectedDate && (
          <div className="mb-6 flex items-center justify-between gap-4 bg-[#F7F4EC] border border-[#9C7434]/20 rounded-xl px-4 py-3" data-testid="date-filter-banner">
            <div className="flex items-center gap-3 text-sm text-[#1F3D2B]">
              <CalendarIcon className="w-4 h-4 text-[#9C7434]" />
              <span>
                Showing experiences for <strong className="font-semibold">{formatPretty(selectedDate)}</strong>
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearDate}
              className="text-xs text-[#4A5D50] hover:text-[#1F3D2B] shrink-0"
              data-testid="date-filter-clear-banner"
            >
              <X className="w-3 h-3 mr-1" /> Clear date
            </Button>
          </div>
        )}

        {/* Events Grid */}
        {loading ? (
          <div className="text-center text-[#4A5D50] py-12">Loading events...</div>
        ) : (
          <>
            {events.length === 0 ? (
              selectedDate ? (
                <div className="py-16 px-6 bg-white rounded-2xl border border-[#E5E9E6]" data-testid="no-events-for-date">
                  <div className="max-w-xl mx-auto text-center">
                    <CalendarIcon className="w-12 h-12 mx-auto text-[#9C7434]/50 mb-4" />
                    <h2 className="font-serif text-2xl sm:text-3xl text-[#1F3D2B] mb-3">
                      No events available on this date.
                    </h2>
                    <p className="text-stone-600 mb-6 leading-relaxed text-sm sm:text-base">
                      Try another day or view all upcoming experiences.
                    </p>
                    <Button
                      onClick={clearDate}
                      className="bg-[#1F3D2B] hover:bg-[#1F3D2B]/90 text-white rounded-full"
                      data-testid="view-all-upcoming-btn"
                    >
                      View all upcoming experiences
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="py-20 px-6" data-testid="no-events-message">
                <div className="max-w-2xl mx-auto text-center">
                  <div className="inline-block px-4 py-1 bg-[#1F3D2B]/5 text-[#1F3D2B] text-xs tracking-[0.2em] uppercase rounded-full mb-4">
                    Launching Soon
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#1F3D2B] mb-4">
                    Our first experiences are being curated.
                  </h2>
                  <p className="text-stone-600 mb-8 leading-relaxed">
                    VinePass curates intimate wine farm experiences across South Africa. Be the first to know when bookings open.
                  </p>
                  <NotifyMeForm />
                  <p className="text-xs text-stone-400 mt-6">No spam. One email when our first experience goes live.</p>
                </div>
              </div>
              )
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="events-grid">
                {events.map((event) => (
                  <div
                    key={event.event_id}
                    className="event-card bg-white border border-[#E5E9E6] shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/events/${event.event_id}`)}
                    data-testid={`event-card-${event.event_id}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6">
                      <div className="mb-2">
                        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4E7C59]">
                          {event.category}
                        </span>
                      </div>
                      <h3
                        className="text-xl sm:text-2xl leading-snug font-medium text-[#112318] mb-3"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {event.title}
                      </h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-[#4A5D50]">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {event.date} at {event.time}
                        </div>
                        <div className="flex items-center text-sm text-[#4A5D50]">
                          <MapPin className="w-4 h-4 mr-2" />
                          {event.location}
                        </div>
                      </div>
                      <p className="text-sm text-[#4A5D50] line-clamp-2 mb-4">
                        {event.description}
                      </p>
                      <Button
                        className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full font-medium transition-all"
                        data-testid={`view-details-button-${event.event_id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventsPage;